import apiClient, { retryRequest } from "./axios";
import { formatDateParam } from "./apiUtils";

/**
 * 航班运行API
 * 处理与航班执行计划(flight_operation)相关的操作
 */
export const flightOperationApi = {
    /**
     * 获取航班运行列表
     * @param {Object} params 查询参数 (支持分页、过滤和排序)
     * @param {number} params.page 页码，从1开始
     * @param {number} params.page_size 每页数量，最大500
     * @param {string} params.aircraft_reg 飞机注册号，多值用逗号分隔，如 "B-1234,B-5678"
     * @param {string} params.status 航班状态，多值用逗号分隔，如 "SCHEDULED,ACTIVE"
     * @param {string} params.start_date 开始日期，格式 YYYY-MM-DD
     * @param {string} params.end_date 结束日期，格式 YYYY-MM-DD
     * @param {string} params.date_basis 日期范围筛选口径，effective 或 std
     * @returns {Promise<Object>} 航班数据列表 {items: Array, total: number}
     */
    getFlightOperations: async (params = {}) => {
        try {
            // 确保日期参数格式化为后端期望的格式 (YYYY-MM-DD)
            const formattedParams = {
                ...params,
                start_date: formatDateParam(params.start_date),
                end_date: formatDateParam(params.end_date),
            };

            // 确保page_size不超过后端允许的最大值
            if (formattedParams.page_size && formattedParams.page_size > 500) {
                console.warn("page_size超过500可能导致后端拒绝请求，自动调整为500");
                formattedParams.page_size = 500;
            }

            const response = await apiClient.get("/v1/flight-operations/search", { params: formattedParams });
            return response;
        } catch (error) {
            console.error("获取航班运行数据失败:", error);
            throw error;
        }
    },

    /**
     * 获取带事务信息的航班运行列表 (优化API调用)
     * @param {Object} params 查询参数 (支持分页、过滤和排序)
     * @param {number} params.page 页码，从1开始
     * @param {number} params.page_size 每页数量，最大500
     * @param {string} params.aircraft_reg 飞机注册号，多值用逗号分隔，如 "B-1234,B-5678"
     * @param {string} params.status 航班状态，多值用逗号分隔，如 "SCHEDULED,ACTIVE"
     * @param {string} params.start_date 开始日期，格式 YYYY-MM-DD
     * @param {string} params.end_date 结束日期，格式 YYYY-MM-DD
     * @returns {Promise<Object>} 航班数据及事务列表 {items: Array, total: number}
     */
    getFlightOperationsWithTransactions: async (params = {}) => {
        try {
            // 确保日期参数格式化为后端期望的格式 (YYYY-MM-DD)
            const formattedParams = {
                ...params,
                start_date: formatDateParam(params.start_date),
                end_date: formatDateParam(params.end_date),
            };

            // 确保page_size不超过后端允许的最大值
            if (formattedParams.page_size && formattedParams.page_size > 500) {
                console.warn("page_size超过500可能导致后端拒绝请求，自动调整为500");
                formattedParams.page_size = 500;
            }

            const response = await apiClient.get("/v1/flight-operations/with-transactions", { params: formattedParams });
            return response;
        } catch (error) {
            console.error("获取航班运行数据及事务信息失败:", error);
            throw error;
        }
    },

    /**
     * 获取单个航班运行详情
     * @param {number} id 航班ID
     * @returns {Promise<Object>} 航班详情
     */
    getFlightOperationById: async (id) => {
        try {
            const response = await retryRequest(
                () => apiClient.get(`/v1/flight-operations/single/${id}`),
                3, // 最大重试3次
                1000, // 初始延迟1秒
            );
            return response;
        } catch (error) {
            console.error(`获取航班ID=${id}的详情失败:`, error);
            throw error;
        }
    },

    /**
     * 应用航班变更命令
     * 调用统一的变更处理端点
     * @param {Object} command 变更命令对象，包含:
     *   - flight_operation_id: 航班ID (单个ID或数组)
     *   - change_type: 变更类型 (如 UPDATE_FLIGHT_INFO, UPDATE_FLIGHT_STATUS 等)
     *   - payload: 变更内容
     *   - operator: 操作者信息
     *   - trigger_source: 触发源
     *   - remarks: 备注
     * @param {Object} config axios配置选项，如 { _silentError: true }
     * @returns {Promise<Object>} 处理结果
     */
    applyChanges: async (command, config = {}) => {
        try {
            // 后端期望的端点是 /flight-operations/changes
            const response = await apiClient.post(`/v1/flight-operations/changes`, command, config);
            return response;
        } catch (error) {
            console.error(`应用航班变更失败 (Type: ${command?.change_type}):`, error);
            throw error;
        }
    },

    /**
     * 处理飞机备降（结构性行段变更）
     *
     * 依据设计文档 `7.0 飞机备降与衍生航段设计.md` §8.2，备降不沿用 UPDATE_FLIGHT_INFO，
     * 而是使用专用 `PROCESS_DIVERSION` 变更命令，经 ChangeDispatcherService 路由到 DiversionService。
     * 该操作为原子性事务：原 a-b 占位化 + 生成衍生段 a-c（真实执飞）与 c-b（续飞）+ 关联事务迁移。
     *
     * @param {number} flightId 原航班（a-b）ID
     * @param {Object} diversionPayload 备降参数
     * @param {string} diversionPayload.diversion_airport 备降机场 IATA 代码 c（必填）
     * @param {string|null} diversionPayload.diverted_at ATA@c ISO 时间字符串，可空（飞行中记录备降时）
     * @param {string} diversionPayload.diversion_reason `[代码] 描述` 格式原因文本
     * @param {string} diversionPayload.recovery_std c-b 续飞段 STD ISO 时间字符串（必填）
     * @param {Object} meta 元信息
     * @param {number} meta.operator 操作者用户ID
     * @param {string} [meta.triggerSource="DIVERSION_MODAL"] 触发源标识
     * @param {Object} [config] axios 配置选项，如 { _silentError: true }
     * @returns {Promise<Object>} 处理结果
     */
    processDiversion: async (flightId, diversionPayload, meta, config = {}) => {
        const command = {
            flight_operation_id: Number(flightId),
            change_type: "PROCESS_DIVERSION",
            payload: diversionPayload,
            operator: meta.operator,
            trigger_source: meta.triggerSource || "DIVERSION_MODAL",
            timestamp: new Date().toISOString(),
        };
        // 复用统一变更端点，仅 change_type 不同
        return flightOperationApi.applyChanges(command, config);
    },

    /**
     * 获取航班事件日志
     * @param {number} flightId 航班ID
     * @param {Object} params 查询参数 (page_size, page, event_type等)
     * @returns {Promise<Object>} 事件日志列表 {items: Array, total: number}
     */
    getFlightEventLogs: async (flightId, params = {}) => {
        try {
            const response = await apiClient.get(`/v1/flight-operations/${flightId}/event-logs`, { params });
            return response;
        } catch (error) {
            console.error(`获取航班ID=${flightId}的事件日志失败:`, error);
            throw error;
        }
    },

    /**
     * 获取航班数据质量校验警告汇总
     * @param {Object} params 查询参数
     * @param {string} params.start_date 开始日期 YYYY-MM-DD
     * @param {string} params.end_date 结束日期 YYYY-MM-DD
     * @param {string} params.aircraft_reg 飞机注册号（可选）
     * @param {string} params.level 级别过滤 ERROR/WARNING/INFO（可选）
     * @returns {Promise<Object>} 校验警告汇总
     */
    getValidationWarnings: async (params = {}) => {
        try {
            const formattedParams = {
                ...params,
                start_date: formatDateParam(params.start_date),
                end_date: formatDateParam(params.end_date),
            };
            const response = await apiClient.get("/v1/flight-operation/validation-warnings", { params: formattedParams });
            return response;
        } catch (error) {
            console.error("获取校验警告失败:", error);
            throw error;
        }
    },

    /**
     * 触发批量数据质量扫描
     * @param {Object} data 扫描参数
     * @param {string} data.start_date 开始日期 YYYY-MM-DD
     * @param {string} data.end_date 结束日期 YYYY-MM-DD
     * @param {string} data.aircraft_reg 飞机注册号（可选）
     * @returns {Promise<Object>} 扫描结果
     */
    runValidationScan: async (data) => {
        try {
            const response = await apiClient.post("/v1/flight-operation/validation-scan", {
                ...data,
                start_date: formatDateParam(data.start_date),
                end_date: formatDateParam(data.end_date),
            });
            return response;
        } catch (error) {
            console.error("批量扫描失败:", error);
            throw error;
        }
    },
};

export default flightOperationApi;
